import { PaletteColorOptions, createTheme } from "@mui/material/styles";
import { ITheme } from "contexts/theme";
import { getRGBA } from "utils/color";
import { conduccoThemeSetup } from "./theme-list";

declare module "@mui/material/styles" {
  interface CustomPalette {
    anger: PaletteColorOptions;
  }
  interface Palette extends CustomPalette {}
  interface PaletteOptions extends CustomPalette {}
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    anger: true;
  }
}

export const generateTheme = (themeColors: ITheme) => {
  return createTheme(
    {
      palette: {
        anger: {
          main: "#FF0000",
          light: "#FF0000",
          dark: "#FF0000",
          contrastText: "#FFFFFF",
        },
        primary: {
          main: themeColors.main,
          light: themeColors.light,
          dark: themeColors.dark,
          contrastText: "#FFFFFF",
        },
        secondary: {
          main: "#5A5A63",
          light: "#E4E4E4",
          dark: "#7E2A2C",
          contrastText: "#191C1A",
        },
        error: {
          main: "#BA1A1A",
        },

        success: {
          main: "#2E7D32",
        },

        warning: {
          main: "#EF6C00",
        },

        info: {
          main: themeColors.info.main,
          light: themeColors.info.light,
          dark: themeColors.info.dark,
          contrastText: "#FFFFFF1A",
        },

        background: {
          default: "#FAFAFA",
        },
      },
      typography: {
        fontSize: 14,
        fontFamily: "Sora, sans-serif",
        allVariants: {
          color: "#212121",
        },
        button: {
          textTransform: "none",
        },
      },

      shape: {
        borderRadius: 4,
      },

      components: {
        MuiCssBaseline: {
          styleOverrides: {
            ".MuiSkeleton-root": {
              borderRadius: 2,
            },
            ".MuiButton-contained": {
              backgroundColor: `${themeColors.main} !important`,
              height: "56px",
              color: "white !important",
              fontWeight: "bold",
            },
            ".MuiButton-contained.Mui-disabled": {
              backgroundColor: "rgba(25, 28, 26, 0.12) !important",

              color: "gray !important",
              fontWeight: "bold",
            },

            ".MuiButton-outlined.Mui-disabled": {
              opacity: "0.2",
            },
            ".MuiFormControl-root": {
              marginTop: "0 !important",
            },
            ".MuiInputLabel-root": {
              backgroundColor: "#FAFAFA !important",
              color: "primary.dark !important",
              lineHeight: "18px !important",
              fontSize: "18px !important",
              fontWeight: "400 !important",
            },
            ".MuiInputLabel-outlined.MuiInputLabel-shrink": {
              backgroundColor: "white",
              paddingLeft: "5px",
              paddingRight: "5px",
            },
            ".MuiOutlinedInput-root": {
              height: "56px",
              padding: "16px 14px",
              borderColor: "#707974",
            },
            "*::-webkit-scrollbar": {
              backgroundColor: "#FAFAFA",
              width: "0.3em",
            },
            "*::-webkit-scrollbar-track": {
              WebkitBoxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
            },
            "*::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,.1)",
            },
            ".MuiToolbar-root": {
              height: "55px !important",
              minHeight: "55px !important",
            },
            ".MuiToolbar-root.MuiToolbar-gutters": {
              paddingLeft: 0,
              marginLeft: 0,
            },
            ".MuiTableCell-root": {
              padding: "10px !important",
              lineHeight: "2rem !important",
              backgroundColor: "#FAFAFA !important",
              borderBottom: `1px solid ${getRGBA("#000000", 0.12)} !important`,
            },
            ".MuiTableCell-head": {
              padding: "20px 10px 20px 10px !important",
              fontWeight: "bold",
              lineHeight: "1rem !important",
              color: "#212121 !important",
              backgroundColor: "#FAFAFA !important",
              borderBottom: `1px solid ${getRGBA("#000000", 0.12)} !important`,
            },
            ".MuiTableSortLabel-root": {
              color: "#212121 !important",
            },
            ".MuiTableSortLabel-icon": {
              color: "#212121 !important",
            },
            ".MuiGrid-root": {
              flexGrow: 1,
              marginTop: "0px !important",
              marginBottom: "0px !important",
              width: "100%",
            },
            ".MuiGrid-item": {},
            ".MuiSvgIcon-root": {
              color: "#212121",
            },
            textarea: {
              resize: "none",
              borderRadius: "4px !important",
              width: "100%",
              marginTop: "10px !important",
              padding: "10px",
              outline: "none !important",
              border: "1px solid #C4C4C4",
              fontFamily: `"Inter", sans-serif`,
              fontSize: 14,
              color: "#605F6A",
              "&:focus": {
                border: "2px solid #2614FE",
              },
            },
          },
        },

        MuiTextField: {
          defaultProps: {
            variant: "outlined",
            size: "small",
            margin: "dense",
            autoComplete: "off",
            fullWidth: true,
          },
        },

        MuiInputBase: {
          styleOverrides: {
            input: {
              "&:-webkit-autofill": {
                transitionDelay: "9999s",
                transitionProperty: "background-color, color",
              },
              padding: "0 !important",
            },
          },
        },

        MuiInputLabel: {
          defaultProps: {
            shrink: true,
          },
        },

        MuiButtonBase: {
          defaultProps: {},
        },

        MuiTooltip: {
          styleOverrides: {
            arrow: {
              color: getRGBA("#616161E5", 0.9),
            },
            tooltip: {
              backgroundColor: getRGBA("#616161E5", 0.9),
              font: "Nunito Sans",
              fontWeight: 600,
              fontSize: "0.725rem",
              lineHeight: "1rem",
              padding: "0.5rem 0.75rem",
              borderRadius: "0.25rem",
              color: "#FFFFFF",
            },
          },
        },

        MuiCheckbox: {
          styleOverrides: {
            root: {
              color: themeColors.main,
            },
          },
        },

        MuiIcon: {
          styleOverrides: {
            root: {
              color: "#212121",
              fontSize: "2rem",
            },
          },
        },
      },

      zIndex: {
        appBar: 1200,
        drawer: 1100,
      },

      transitions: {
        duration: {
          standard: 300,
        },
      },
    },
    {}
  );
};

export const defaultTheme = generateTheme(conduccoThemeSetup);
